import { useMemo } from 'react'
import { UserDocument } from '@/features/user/your-practice/documents/lib/userDocuments.slice'
import { UserDocumentCategoryIdentifier } from '@/features/admin/user-document-categories/userDocumentCategory.constants'
import { useDocuments } from '../use-documents'
import {
  IDocumentsStepContext,
  IFixedAssetDepreciationStepContext,
  OtherDocumentsStepContext,
  ContractorDocumentsStepContext,
  PayrollDocumentsStepContext,
} from '../../end-of-year-review/types'
import { IMerchantDocument } from '@/features/bookkeeping/end-of-year-review/processing-fees-documents/documents-map'

const HEARD_PROVIDER = 'Heard'

export const ASSET_RECEIPTS_LABEL = 'Receipts for assets over $2,500'

export type UnresolvedDocumentSection =
  | 'payroll'
  | 'contractor'
  | 'processingFees'
  | 'assets'
  | 'taxReturn'

export interface IUnresolvedDocument {
  section: UnresolvedDocumentSection
  label: string
  canMarkNotRelevant: boolean
}

type GetStatusFn = (
  categoryCode: string,
  provider?: string,
  isMarkedNotRelevant?: (
    categoryInternalName: string,
    provider?: string,
    documentName?: string
  ) => boolean | undefined,
  documentName?: string
) => 'success' | 'none'

export const isMarkedNotRelevant = (
  stepContext:
    | PayrollDocumentsStepContext
    | ContractorDocumentsStepContext
    | IDocumentsStepContext
    | undefined,
  categoryInternalName: string,
  provider?: string,
  documentName?: string
): boolean => {
  const filterDocs = stepContext?.documents?.find((doc) => {
    return (
      doc.categoryCode === categoryInternalName &&
      (provider === undefined || doc.provider === provider) &&
      (documentName === undefined || doc.name === documentName)
    )
  })

  return filterDocs?.notRelevant !== undefined
}

/**
 * Check if all payroll providers have required documents (W-3 and payroll journal)
 */
export const checkPayrollEligibility = (
  payrollProviders: string[] | null,
  isFollowUp: boolean,
  getPayrollStatus: GetStatusFn,
  stepContext: PayrollDocumentsStepContext | undefined,
  categoryIdentifiers: {
    formW3: string
    payrollJournal: string
  }
) => {
  const payrollProviderValues =
    payrollProviders !== null ? payrollProviders : []

  // If this is a followup and the docs weren't auto uploaded, these should show because a user can manually request them
  const filteredPayrolls = isFollowUp
    ? payrollProviderValues
    : payrollProviderValues.filter((p) => p !== HEARD_PROVIDER)

  return filteredPayrolls.every((provider) => {
    const w3Status = getPayrollStatus(
      categoryIdentifiers.formW3,
      provider,
      (categoryInternalName, provider) =>
        isMarkedNotRelevant(stepContext, categoryInternalName, provider)
    )

    const journalStatus = getPayrollStatus(
      categoryIdentifiers.payrollJournal,
      provider,
      (categoryInternalName, provider) =>
        isMarkedNotRelevant(stepContext, categoryInternalName, provider)
    )

    return w3Status === 'success' && journalStatus === 'success'
  })
}

/**
 * Check if all contractor providers have required payment reports
 */
export const checkContractorEligibility = (
  contractorProviders: string[] | null,
  getContractorStatus: GetStatusFn,
  stepContext: ContractorDocumentsStepContext | undefined,
  contractorPaymentsReportIdentifier: string
): boolean => {
  if (contractorProviders === null) {
    return true
  }

  return contractorProviders
    .filter((p) => p !== HEARD_PROVIDER)
    .every((provider) => {
      const status = getContractorStatus(
        contractorPaymentsReportIdentifier,
        provider,
        (categoryInternalName, provider) =>
          isMarkedNotRelevant(stepContext, categoryInternalName, provider)
      )

      return status === 'success'
    })
}

/**
 * Check if Heard contractor requirements are met in followup mode
 * (requires either payments report OR 1099-NEC)
 */
export const checkHeardContractorEligibility = (
  isFollowUp: boolean,
  getContractorStatus: GetStatusFn,
  categoryIdentifiers: {
    contractorPaymentsReport: string
    form1099nec: string
  }
): boolean => {
  if (!isFollowUp) {
    return true
  }

  const paymentsReportStatus = getContractorStatus(
    categoryIdentifiers.contractorPaymentsReport,
    HEARD_PROVIDER
  )

  const form1099Status = getContractorStatus(
    categoryIdentifiers.form1099nec,
    HEARD_PROVIDER
  )

  return paymentsReportStatus === 'success' || form1099Status === 'success'
}

/**
 * Check if all unique merchants have required fee documents
 */
export const checkProcessingFeesEligibility = (
  uniqueDocumentMerchants: IMerchantDocument[],
  getFeesStatus: GetStatusFn,
  stepContext: IDocumentsStepContext | undefined
): boolean => {
  return uniqueDocumentMerchants.every(
    ({ merchantName, documentCategoryName, documentName }) => {
      const status = getFeesStatus(
        documentCategoryName,
        merchantName,
        (categoryInternalName, provider, docName) =>
          isMarkedNotRelevant(
            stepContext,
            categoryInternalName,
            provider,
            docName
          ),
        documentName
      )

      return status === 'success'
    }
  )
}

/**
 * Check if asset receipts match the number of transactions without receipts
 * Automatically passes if there are no transactions without receipts
 */
export const checkAssetsEligibility = (
  transactionsWithoutReceipts: ITransaction[],
  assetReceipts: UserDocument[] | null
): boolean => {
  return (
    transactionsWithoutReceipts.length === 0 ||
    (assetReceipts !== null &&
      assetReceipts.length === transactionsWithoutReceipts.length)
  )
}

/**
 * Check if previous tax returns are present when required (multi-year S-Corp)
 */
export const checkTaxReturnsEligibility = (
  isMultiYearSCorp: boolean,
  previousTaxReturnStatus: 'success' | 'none' | undefined,
  uploadStatus: 'success' | 'none'
): boolean => {
  return (
    !isMultiYearSCorp ||
    previousTaxReturnStatus === 'success' ||
    uploadStatus === 'success'
  )
}

export interface ITransaction {
  receipts?: Array<{ documentId: number }>
}

export interface IDocumentEligibilityParams {
  stepContexts: {
    payroll?: PayrollDocumentsStepContext
    contractor?: ContractorDocumentsStepContext
    fees?: IDocumentsStepContext
    assets?: IFixedAssetDepreciationStepContext
    tax?: IDocumentsStepContext
    other?: OtherDocumentsStepContext
  }
  payrollProviders: string[] | null
  contractorProviders: string[] | null
  uniqueDocumentMerchants: IMerchantDocument[]
  transactionsWithoutReceipts: ITransaction[]
  documents: UserDocument[] // User-uploaded documents

  /**
   * Whether this is a multi-year S-Corp requiring previous tax returns
   */
  isMultiYearSCorp: boolean

  /**
   * Whether this is a followup review (requires Heard provider checks)
   */
  isFollowUp?: boolean

  categoryIdentifiers: {
    formW3: string
    payrollJournal: string
    contractorPaymentsReport: string
    form1099nec: string
  }
  shouldShowTax: boolean
}

export interface IDocumentEligibilityResult {
  canContinue: boolean
  unresolvedDocuments: IUnresolvedDocument[]
}

/**
 * Hook for calculating document eligibility to continue to next step.
 *
 * This hook encapsulates the eligibility logic to determine whether a user
 * has uploaded all required documents across 6 categories:
 * - Payroll (W-3, payroll journal)
 * - Contractor payments (1099-NEC, payments report)
 * - Processing fees
 * - Assets over $2,500
 * - Previous tax returns (if multi-year S-Corp)
 * - Other documents
 *
 * **Eligibility Rules:**
 * 1. **Payroll**: All providers must have W-3 AND payroll journal (or marked not relevant)
 *    - In followup mode: Also checks Heard provider
 *    - Can be skipped if "Other Documents" are uploaded
 * 2. **Contractor**: All providers must have contractor payments report
 *    - In followup mode: Heard provider must have payments report OR 1099-NEC
 * 3. **Processing Fees**: All unique merchants must have fee documents
 * 4. **Assets**: Number of receipts must equal number of transactions without receipts
 * 5. **Tax Returns**: If multi-year S-Corp, must have previous year tax return
 * 6. **Other**: Checked as alternative to payroll documents
 *
 * **Usage Example:**
 * ```tsx
 * const ReviewDocumentsContainer = () => {
 *   const { canContinue } = useDocumentEligibility({
 *     stepContexts: {
 *       payroll: payrollStepContext,
 *       contractor: contractorStepContext,
 *       fees: feesStepContext,
 *       assets: assetStepContext,
 *       tax: taxStepContext,
 *       other: otherDocsContext,
 *     },
 *     payrollProviders,
 *     contractorProviders,
 *     uniqueDocumentMerchants,
 *     transactionsWithoutReceipts,
 *     documents,
 *     isMultiYearSCorp,
 *     isFollowUp,
 *     categoryIdentifiers: {
 *       formW3: UserDocumentCategoryIdentifier.formW3,
 *       payrollJournal: PrefixedUserDocumentCategoryIdentifier.payrollJournal(year),
 *       contractorPaymentsReport: PrefixedUserDocumentCategoryIdentifier.contractorPaymentsReport(year),
 *       form1099nec: UserDocumentCategoryIdentifier.form1099nec,
 *     },
 *   })
 *
 *   return (
 *     <div>
 *       <ContinueButton disabled={!canContinue} />
 *     </div>
 *   )
 * }
 * ```
 *
 * @param params - Eligibility calculation parameters
 * @returns Object with canContinue boolean
 */
export const useDocumentEligibility = ({
  stepContexts,
  payrollProviders,
  contractorProviders,
  uniqueDocumentMerchants,
  transactionsWithoutReceipts,
  documents,
  isMultiYearSCorp,
  isFollowUp = false,
  categoryIdentifiers,
  shouldShowTax,
}: IDocumentEligibilityParams) => {
  // Set up document status hooks for each category
  const { getStatus: getPayrollStatus } = useDocuments({
    stepContextDocuments: stepContexts.payroll?.documents,
    userDocuments: documents,
  })

  const { getStatus: getContractorStatus } = useDocuments({
    stepContextDocuments: stepContexts.contractor?.documents,
    userDocuments: documents,
  })

  const { getStatus: getFeesStatus } = useDocuments({
    stepContextDocuments: stepContexts.fees?.documents,
    userDocuments: documents,
  })

  const { getStatus: getPreviousTaxReturnStatus } = useDocuments({
    stepContextDocuments: stepContexts.tax?.documents,
    userDocuments: documents,
  })

  const assetReceipts = useMemo(() => {
    if (!stepContexts.assets) return null

    const uploadedReceipts = stepContexts.assets?.receipts ?? []

    return documents.filter((doc) =>
      uploadedReceipts.some((receipt) => receipt.documentId === doc.id)
    )
  }, [stepContexts.assets, documents])

  const previousTaxReturnStatus = useMemo(() => {
    const status = getPreviousTaxReturnStatus(
      UserDocumentCategoryIdentifier.finalReturnForm1120s,
      '' // Provider-less document - empty string or undefined both work
    )

    return status === undefined ? 'success' : status
  }, [getPreviousTaxReturnStatus])

  const uploadStatus = useMemo(() => {
    if (!shouldShowTax) {
      return 'success'
    }

    const previousYearTaxDocContext = stepContexts.tax?.documents?.find(
      (doc) =>
        doc.categoryCode === UserDocumentCategoryIdentifier.finalReturnForm1120s
    )

    if (!previousYearTaxDocContext) {
      return 'none'
    }

    // Check if there's a matching user document
    const hasUserDocument = documents.some(
      (userDoc) => userDoc.id === previousYearTaxDocContext.documentId
    )

    return hasUserDocument ? 'success' : 'none'
  }, [shouldShowTax, stepContexts.tax?.documents, documents])

  const uploadOtherDocsStatus = useMemo(() => {
    // Aggregate otherDocumentIds from all contexts where they can be uploaded
    const allOtherDocumentIds = [
      ...(stepContexts.payroll?.otherDocumentIds ?? []),
      ...(stepContexts.contractor?.otherDocumentIds ?? []),
      ...(stepContexts.assets?.otherDocumentIds ?? []),
      ...(stepContexts.other?.documentIds ?? []),
    ]

    const filteredDocuments = documents.filter((doc) =>
      allOtherDocumentIds.includes(doc.id)
    )

    return filteredDocuments.length > 0 ? 'success' : 'none'
  }, [
    documents,
    stepContexts.payroll,
    stepContexts.contractor,
    stepContexts.assets,
    stepContexts.other,
  ])

  const unresolvedDocuments = useMemo(() => {
    const unresolved: IUnresolvedDocument[] = []

    const payrollProviderValues = payrollProviders ?? []
    const payrollProvidersToCheck = isFollowUp
      ? payrollProviderValues
      : payrollProviderValues.filter((p) => p !== HEARD_PROVIDER)

    if (uploadOtherDocsStatus !== 'success') {
      payrollProvidersToCheck.forEach((provider) => {
        const isResolved = checkPayrollEligibility(
          [provider],
          isFollowUp,
          getPayrollStatus,
          stepContexts.payroll,
          {
            formW3: categoryIdentifiers.formW3,
            payrollJournal: categoryIdentifiers.payrollJournal,
          }
        )
        if (!isResolved) {
          unresolved.push({
            section: 'payroll',
            label: `${provider} payroll documents`,
            canMarkNotRelevant: true,
          })
        }
      })
    }

    ;(contractorProviders ?? [])
      .filter((p) => p !== HEARD_PROVIDER)
      .forEach((provider) => {
        const isResolved = checkContractorEligibility(
          [provider],
          getContractorStatus,
          stepContexts.contractor,
          categoryIdentifiers.contractorPaymentsReport
        )
        if (!isResolved) {
          unresolved.push({
            section: 'contractor',
            label: `${provider} contractor payments report`,
            canMarkNotRelevant: true,
          })
        }
      })

    const isHeardContractorResolved = checkHeardContractorEligibility(
      isFollowUp,
      getContractorStatus,
      {
        contractorPaymentsReport: categoryIdentifiers.contractorPaymentsReport,
        form1099nec: categoryIdentifiers.form1099nec,
      }
    )
    if (!isHeardContractorResolved) {
      unresolved.push({
        section: 'contractor',
        label: `${HEARD_PROVIDER} contractor payments report or 1099-NEC`,
        canMarkNotRelevant: false,
      })
    }

    uniqueDocumentMerchants.forEach((merchant) => {
      const isResolved = checkProcessingFeesEligibility(
        [merchant],
        getFeesStatus,
        stepContexts.fees
      )
      if (!isResolved) {
        unresolved.push({
          section: 'processingFees',
          label: `${merchant.merchantName} - ${merchant.documentName}`,
          canMarkNotRelevant: true,
        })
      }
    })

    if (!checkAssetsEligibility(transactionsWithoutReceipts, assetReceipts)) {
      unresolved.push({
        section: 'assets',
        label: ASSET_RECEIPTS_LABEL,
        canMarkNotRelevant: false,
      })
    }

    if (
      !checkTaxReturnsEligibility(
        isMultiYearSCorp,
        previousTaxReturnStatus,
        uploadStatus
      )
    ) {
      unresolved.push({
        section: 'taxReturn',
        label: 'Previous year tax return',
        canMarkNotRelevant: false,
      })
    }

    return unresolved
  }, [
    payrollProviders,
    contractorProviders,
    uniqueDocumentMerchants,
    transactionsWithoutReceipts,
    assetReceipts,
    isMultiYearSCorp,
    isFollowUp,
    stepContexts,
    categoryIdentifiers,
    getPayrollStatus,
    getContractorStatus,
    getFeesStatus,
    previousTaxReturnStatus,
    uploadStatus,
    uploadOtherDocsStatus,
  ])

  return {
    canContinue: unresolvedDocuments.length === 0,
    unresolvedDocuments,
  }
}
